import { SpacedForwardSlash } from '@mews-commander/ui';
import { Divider, Body, Stack, Typography } from '@optimus-web/core';
import { UnicodeEntity } from '@mews/enums';
import { ProductCheckListDataRow } from '../../types';
import { ProductCheckListDataTestIds } from '../../constants';
import { useFilteredProductsData } from './utils/useFilteredProductsData';

interface TotalCellProps {
    row: ProductCheckListDataRow;
    active?: boolean;
    ageCategoryIdentifier?: string;
}

export const TotalCell: React.FC<TotalCellProps> = ({ row, active, ageCategoryIdentifier }) => {
    const filteredProducts = useFilteredProductsData(row.productsData);

    if (!filteredProducts.length) {
        return null;
    }

    return (
        <Stack vertical fill alignment="center" spacing="100" data-test-id={ProductCheckListDataTestIds.cellTotal}>
            {filteredProducts.map(({ data, totalCount, claimed, claimedByAgeCategories }, index, self) => {
                const commonProps = {
                    key: data.id,
                    productId: data.id,
                    active,
                    showDivider: index !== self.length - 1,
                };

                if (ageCategoryIdentifier) {
                    const categoryRecord = claimedByAgeCategories[ageCategoryIdentifier];

                    return categoryRecord ? (
                        <TotalRecord {...commonProps} claimed={categoryRecord.claimed} total={categoryRecord.total} />
                    ) : (
                        <>
                            <Typography
                                variant={!active ? 'tertiary' : 'primary'}
                                textStyle="bodyMediumStrong"
                                as="div"
                            >
                                {UnicodeEntity.NoBreakSpace}
                            </Typography>
                            {commonProps.showDivider && <Divider />}
                        </>
                    );
                }

                return <TotalRecord {...commonProps} claimed={claimed} total={totalCount} />;
            })}
        </Stack>
    );
};

const TotalRecord: React.FC<{
    claimed: number;
    total: number;
    showDivider: boolean;
    active: boolean | undefined;
    productId: string;
}> = ({ claimed, total, showDivider, active, productId }) => (
    <>
        <Stack alignment="end" data-test-product-id={productId}>
            <Body size="small" variant={!active ? 'tertiary' : 'primary'}>
                <span data-test-id={ProductCheckListDataTestIds.claimed}>{claimed}</span>
                {SpacedForwardSlash}
            </Body>
            <Typography
                variant={!active ? 'tertiary' : 'primary'}
                textStyle="bodyMediumStrong"
                data-test-id={ProductCheckListDataTestIds.total}
                as="div"
            >
                {total}
            </Typography>
        </Stack>

        {showDivider && <Divider />}
    </>
);
